// Referral physicians (owner item 3, 2026-10-19): form helpers shared by the
// Add / Edit dialog. The server (v2 api/referralphysicians/validate.py) is the
// authority; these checks only catch the obvious mistakes before a round trip.

export const FIELDS = [
  "doctor_name", "first_name", "last_name", "credentials", "specialty", "npi",
  "phone", "fax", "practice_name", "address1", "address2", "city", "state", "zip",
];

export const OTHER_SPECIALTY = "Other";

export const emptyForm = (name = "") => ({
  doctor_name: name ? withTitle(name) : "Dr. ",
  first_name: "", last_name: "", credentials: "", specialty: "", specialtyOther: "",
  npi: "", phone: "", fax: "", practice_name: "", address1: "", address2: "",
  city: "", state: "", zip: "",
});

// "smith" -> "Dr. smith"; keeps a name that already starts with "Dr".
export function withTitle(name) {
  const s = String(name || "").trim();
  if (!s) return "Dr. ";
  return /^dr\.?\s/i.test(s) ? s : `Dr. ${s}`;
}

export const digits = (v) => String(v || "").replace(/\D/g, "");

// 10 digits -> "(432) 555-0147"; anything else is shown as typed.
export function formatPhone(v) {
  const d = digits(v);
  const ten = d.length === 11 && d[0] === "1" ? d.slice(1) : d;
  if (ten.length !== 10) return String(v || "");
  return `(${ten.slice(0, 3)}) ${ten.slice(3, 6)}-${ten.slice(6)}`;
}

// CMS NPI check digit: Luhn over "80840" + the first 9 digits.
export function npiValid(npi) {
  const s = String(npi || "");
  if (!/^[12]\d{9}$/.test(s)) return false;
  const nums = ("80840" + s.slice(0, 9)).split("").map(Number);
  let total = 0;
  nums.reverse().forEach((d, i) => {
    let x = d;
    if (i % 2 === 0) {
      x *= 2;
      if (x > 9) x -= 9;
    }
    total += x;
  });
  return (10 - (total % 10)) % 10 === Number(s[9]);
}

const phoneOk = (v) => {
  const d = digits(v);
  const ten = d.length === 11 && d[0] === "1" ? d.slice(1) : d;
  return ten.length === 10 && !/[01]/.test(ten[0]) && !/[01]/.test(ten[3]);
};

// The display name without its title has letters (so "Dr. " alone is empty).
export const nameBody = (name) => String(name || "").replace(/^\s*dr\.?\s*/i, "").trim();

export function specialtyValue(form) {
  if (form.specialty === OTHER_SPECIALTY) return (form.specialtyOther || "").trim();
  return (form.specialty || "").trim();
}

// {field: message} for the problems we can see here; {} when the form may be sent.
export function validateForm(form) {
  const e = {};
  const body = nameBody(form.doctor_name);
  if (!body) e.doctor_name = "Enter the doctor's name, e.g. Dr. Patel.";
  else if (/\d/.test(form.doctor_name)) e.doctor_name = "No numbers in the name.";
  else if (/[^\p{L}\s.'’‐‑–—-]/u.test(form.doctor_name)) e.doctor_name = "Letters, spaces, '.', apostrophes and hyphens only.";
  else if (form.doctor_name.trim().length > 60) e.doctor_name = "At most 60 characters.";
  else if (form.doctor_name.trim().split(/\s+/).length > 6) e.doctor_name = "Use the doctor's name, not a sentence.";
  if (!specialtyValue(form)) e.specialty = form.specialty === OTHER_SPECIALTY ? "Type the specialty." : "Choose a specialty.";
  if (form.npi && !npiValid(digits(form.npi))) e.npi = "Not a valid NPI (10 digits, check digit).";
  if (form.phone && !phoneOk(form.phone)) e.phone = "10-digit US number.";
  if (form.fax && !phoneOk(form.fax)) e.fax = "10-digit US number.";
  if (form.state && !/^[A-Za-z]{2}$/.test(form.state.trim())) e.state = "2 letters, e.g. TX.";
  if (form.zip && !/^\d{5}(-\d{4})?$/.test(form.zip.trim())) e.zip = "5 digits or ZIP+4.";
  return e;
}

// The request body: every field, trimmed; the "Other" specialty as typed.
export function toBody(form) {
  const b = {};
  FIELDS.forEach((f) => {
    b[f] = String(form[f] || "").trim();
  });
  b.specialty = specialtyValue(form);
  b.npi = digits(form.npi);
  b.state = b.state.toUpperCase();
  return b;
}

// A physician row from the API -> form values (edit mode).
export function fromPhysician(row, specialties = []) {
  const f = emptyForm();
  FIELDS.forEach((k) => {
    f[k] = row?.[k] ? String(row[k]) : "";
  });
  f.phone = formatPhone(f.phone);
  f.fax = formatPhone(f.fax);
  if (f.specialty && !specialties.includes(f.specialty)) {
    f.specialtyOther = f.specialty;
    f.specialty = OTHER_SPECIALTY;
  }
  return f;
}

// One line under a name in lists: "Cardiology · Midland, TX · NPI 1234567893".
export function physicianSubline(row) {
  const place = [row?.city, row?.state].filter(Boolean).join(", ");
  return [row?.specialty, place, row?.npi ? `NPI ${row.npi}` : ""].filter(Boolean).join(" · ");
}

// The gateway's answer body of a failed call (axios error), or null.
export const errorBody = (err) => (err && err.response && err.response.data) || null;
